export interface CommunityAmenity {
  id: string;
  name: string;
  label?: string;
  headline?: string;
  category:
  | "The Pearl"
  | "Grand Harbour"
  | "Cayman Islands";
  description: string;
  images: string[];
  highlights: string[];
  icon?: string;
  featured: boolean;
}

export const communityAmenities: CommunityAmenity[] = [
  // The Pearl - On-site Amenities
  {
    id: "lagoon-views",
    name: "Crystal Lagoon",
    label: "Crystal Lagoon",
    headline: "Your Private Beach Lagoon.",
    category: "The Pearl",
    description:
      "Welcome to the island's most breathtaking swimming lagoon. Your own slice of coastal bliss. From the serene view of its crystalline waters on your terrace to hosting unforgettable gatherings at the cabana with a gourmet BBQ, this is the vibrant, social heart of the lifestyle you deserve.",
    images: ["/img/lagoon-amenity.jpg", "/img/hero-amenity.jpg"],
    highlights: [
      "Signature lagoon living",
      "Beach-style atmosphere",
      "Cabana gatherings",
      "Terrace views",
    ],
    icon: "🌊",
    featured: true,
  },
  {
    id: "wellness-gym",
    name: "Wellness",
    label: "Wellness",
    headline: "The Architecture of Wellness.",
    category: "The Pearl",
    description:
      "Your active life finds its centre in our beautiful, shared gym 2,500 sq. ft. space of light and crafted timber. This is your foundational space. Radiating out a minute's walk is Grand Harbour’s unparalleled wellness network: from motivating yoga and spin studios to essential recovery with physiotherapists and chiropractors. Every facet of your well-being is supported, effortlessly.",
    images: ["/img/gym1.jpg", "/img/gym2.jpg"],
    highlights: [
      "2,500 sq. ft. shared gym",
      "Light + crafted timber",
      "Nearby studios and recovery",
      "Wellness network access",
    ],
    icon: "💪",
    featured: true,
  },
  {
    id: "community-hub",
    name: "Community Hub",
    label: "Community Hub",
    headline: "The Island's Social Heart, Your Front Yard.",
    category: "The Pearl",
    description:
      "We are creating the neighbourhood's vibrant core: a public square alive with a global food hall, an outdoor bar & restaurant, and an anchor entertainment venue like Clip ‘n Climb. This hub will draw energy and culture from across the island. For you, it's the ultimate convenience and social scene—a constantly evolving amenity where every day offers a new taste, connection, or adventure, right outside your door.",
    images: ["/img/food-hall.jpg"],
    highlights: [
      "Public square + plaza life",
      "Global food hall",
      "Outdoor bar & restaurant",
      "Entertainment anchor",
    ],
    icon: "🍽️",
    featured: true,
  },
  {
    id: "modern-architecture",
    name: "Contemporary Architecture",
    label: "Contemporary Architecture",
    headline: "Designed for the View, Engineered for Living.",
    category: "The Pearl",
    description:
      "We challenge the ordinary. While many residences sacrifice private views, we ensure every bedroom and living space is oriented toward the water and light. This is architecture that understands true luxury: privacy paired with a panorama. With expansive balconies and terraces on every level, the barrier between your refined interior and the inspiring outdoors dissolves.",
    images: ["/img/residence-interior.jpg"],
    highlights: [
      "Water + light orientation",
      "Privacy + panorama",
      "Expansive balconies",
      "Terraces on every level",
    ],
    icon: "🏗️",
    featured: true,
  },
  {
    id: "boat-dock",
    name: "Boat Dock",
    label: "Boat Dock",
    headline: "Your Dock Awaits.",
    category: "The Pearl",
    description:
      "To complete the luxury waterfront experience, a limited number of boat moorings will be offered at a later date. Availability will be exclusive to Pearl residents, adding a valuable dimension to harbour living and ensuring your home remains at the center of life on the water.",
    images: ["/img/dock.jpg"],
    highlights: [
      "Limited moorings (later release)",
      "Exclusive to residents",
      "Harbour living",
      "Life on the water",
    ],
    icon: "🛥️",
    featured: true,
  },
  {
    id: "gardens",
    name: "Gardens",
    label: "Gardens",
    headline: "A Tapestry of Green and Blue.",
    category: "The Pearl",
    description:
      "Life at The Pearl unfolds within a masterfully blended environment where lush gardens meet lagoon vistas. Architecture is softened by flourishing vines, and the community is thoughtfully catered to with dedicated zones: a children's play garden for imagination and a secure dog park for off-leash freedom. This is more than landscaping—it's the thoughtful curation of outdoor living for every generation.",
    images: ["/img/blue-green.jpg"],
    highlights: [
      "Lagoon vistas + lush gardens",
      "Children's play garden",
      "Secure dog park",
      "Outdoor living zones",
    ],
    icon: "🌿",
    featured: true,
  },

  // Grand Harbour - Neighborhood Amenities
  {
    id: "walking-distance-essentials",
    name: "Walking Distance Essentials",
    category: "Grand Harbour",
    description:
      "Experience the 5-minute life with daily essentials within walking distance. Grocery stores, gyms, cafés, and schools are just minutes away, eliminating the need for long commutes and creating a true neighborhood lifestyle.",
    images: [],
    highlights: [
      "Grocery stores - 2 min walk",
      "Gyms - 3 min walk",
      "Cafés - 5 min walk",
      "Schools - 8 min drive",
    ],
    icon: "🚶‍♀️",
    featured: true,
  },
  {
    id: "seven-mile-beach",
    name: "Seven Mile Beach Access",
    category: "Grand Harbour",
    description:
      "Just 12 minutes from world-renowned Seven Mile Beach, one of the Caribbean's most beautiful stretches of sand. Enjoy pristine waters, beachfront dining, and endless recreational activities.",
    images: [],
    highlights: [
      "12-minute drive",
      "World-renowned beach",
      "Beachfront dining",
      "Water sports access",
    ],
    icon: "🏖️",
    featured: true,
  },
  {
    id: "camana-bay",
    name: "Camana Bay Proximity",
    category: "Grand Harbour",
    description:
      "Located just 15 minutes from Camana Bay, Grand Cayman's premier mixed-use development featuring shopping, dining, entertainment, and waterfront activities.",
    images: [],
    highlights: [
      "15-minute drive",
      "Premier shopping destination",
      "Dining and entertainment",
      "Waterfront activities",
    ],
    icon: "🛍️",
    featured: true,
  },
  {
    id: "airport-accessibility",
    name: "Airport Accessibility",
    category: "Grand Harbour",
    description:
      "Owen Roberts International Airport is just 10 minutes away, making travel convenient for residents and investors. Perfect for those who frequently travel for business or leisure.",
    images: [],
    highlights: [
      "10-minute drive",
      "International airport",
      "Easy travel access",
      "Business convenience",
    ],
    icon: "✈️",
    featured: false,
  },
  {
    id: "established-neighborhood",
    name: "Established Neighborhood",
    category: "Grand Harbour",
    description:
      "Grand Harbour is a well-established neighborhood with mature infrastructure, strong rental demand from professionals and families, and a central location between the airport and Seven Mile Beach. This proven location offers stability and growth potential for residents and investors alike.",
    images: [],
    highlights: [
      "Mature infrastructure",
      "Strong rental demand",
      "Central location",
      "Investment stability",
    ],
    icon: "🏘️",
    featured: false,
  },

  // Cayman Islands - Broader Benefits
  {
    id: "tax-benefits",
    name: "Tax Benefits",
    category: "Cayman Islands",
    description:
      "Enjoy significant tax advantages including no income tax, no capital gains tax, no property tax, and no inheritance tax. The Cayman Islands offers one of the most favorable tax environments in the world.",
    images: [],
    highlights: [
      "No income tax",
      "No capital gains tax",
      "No property tax",
      "No inheritance tax",
    ],
    icon: "💰",
    featured: true,
  },
  {
    id: "world-class-beaches",
    name: "World-Class Beaches",
    category: "Cayman Islands",
    description:
      "Experience some of the world's most beautiful beaches with pristine white sand, crystal-clear turquoise waters, and year-round perfect weather for beach activities.",
    images: ["/img/world-class-beach.jpg"],
    highlights: [
      "Pristine white sand",
      "Crystal-clear waters",
      "Year-round perfect weather",
      "Multiple beach options",
    ],
    icon: "🏝️",
    featured: true,
  },
  {
    id: "diving-water-sports",
    name: "Diving & Water Sports",
    category: "Cayman Islands",
    description:
      "Grand Cayman is a world-renowned diving destination with spectacular coral reefs, shipwrecks, and marine life. Enjoy world-class diving, snorkeling, fishing, and water sports.",
    images: [],
    highlights: [
      "World-class diving",
      "Spectacular coral reefs",
      "Shipwreck sites",
      "Abundant marine life",
    ],
    icon: "🤿",
    featured: true,
  },
  {
    id: "international-airport",
    name: "International Airport",
    category: "Cayman Islands",
    description:
      "Owen Roberts International Airport offers direct flights to major cities in the US, Canada, UK, and throughout the Caribbean, making the Cayman Islands easily accessible from around the world.",
    images: ["/img/airport.jpg"],
    highlights: [
      "Direct US flights",
      "Caribbean connections",
      "UK and Canada routes",
      "Global accessibility",
    ],
    icon: "🌍",
    featured: false,
  },
  {
    id: "financial-services-hub",
    name: "Financial Services Hub",
    category: "Cayman Islands",
    description:
      "The Cayman Islands is a leading global financial center with a sophisticated banking system, wealth management services, and a stable regulatory environment trusted by international investors.",
    images: [],
    highlights: [
      "Global financial center",
      "Sophisticated banking",
      "Wealth management",
      "Stable regulations",
    ],
    icon: "🏦",
    featured: false,
  },
  {
    id: "stable-government",
    name: "Stable Government & Safety",
    category: "Cayman Islands",
    description:
      "Enjoy peace of mind with one of the Caribbean's most stable governments, low crime rates, and a safe environment for families and businesses. British Overseas Territory with strong rule of law.",
    images: [],
    highlights: [
      "Stable government",
      "Low crime rates",
      "Safe environment",
      "Strong rule of law",
    ],
    icon: "🛡️",
    featured: false,
  },
  {
    id: "english-speaking",
    name: "English-Speaking Environment",
    category: "Cayman Islands",
    description:
      "As a British Overseas Territory, English is the official language, making it easy for international residents and investors to integrate seamlessly into the community and conduct business.",
    images: [],
    highlights: [
      "Official language",
      "Easy integration",
      "Business-friendly",
      "International community",
    ],
    icon: "🗣️",
    featured: false,
  },
];

export const getAmenitiesByCategory = (
  category: CommunityAmenity["category"]
): CommunityAmenity[] => {
  return communityAmenities.filter((amenity) => amenity.category === category);
};

export const getFeaturedAmenities = (): CommunityAmenity[] => {
  return communityAmenities.filter((amenity) => amenity.featured);
};

export const getAmenityById = (id: string): CommunityAmenity | undefined => {
  return communityAmenities.find((amenity) => amenity.id === id);
};

export const getAllCategories = (): CommunityAmenity["category"][] => {
  return Array.from(
    new Set(communityAmenities.map((amenity) => amenity.category))
  );
};
